/**
 * PATH: packages/email-hub/src/feature/components/email-context-menu/useEmailContextMenuController.ts
 * FILE_ID: EMAILHUB-CONTEXTMENU-CONTROLLER-001
 * PURPOSE: Owns state, hooks, and async orchestration for the menu frame.
 * WHY: Leaves EmailContextMenuProvider as a frame while preserving behavior.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { addressFromEventTarget } from '@quotech/ui'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  getSamKanbanApi,
  pickDefaultLaneId,
  resolveKanbanOrgId,
  useKanbanQuickAllocate,
} from '@quotech/kanban-host'
import {
  buildBoardCreatePayload,
  clearProvisionedLanes,
  type CreateBoardInput,
} from '@quotech/ui/kanban'

import { useToast } from '../../../_internal/components/Toast'
import { useApiAuth } from '../../../_internal/components/ApiAuthGate'
import {
  getQuotechOrgIdOverride,
  getRuntimeQuotechOrgId,
} from '../../../_internal/services/quotechApi'
import { useMarkEmailAsBill } from '../../../_stubs/bills/hooks/useBills'
import { seedFromIntent, useComposeDockStore } from '../../compose/composeDockStore'
import { useEmailActions, type Email } from '../../hooks/useEmails'
import {
  useOrgCustomCategories,
  usePersonalCategories,
} from '../../hooks/useEmailLabelPrefs'
import type {
  ContextMenuPosition,
  EmailContextMenuProviderProps,
  LinkModalState,
  RequestRuleState,
} from './types'

export function useEmailContextMenuController({
  onRefetch,
  onPatchLocal,
  onResolvedLocal,
  inviteMembers,
  resolveContextIds,
}: Omit<EmailContextMenuProviderProps, 'children'>) {
  const [ctxMenu, setCtxMenu] = useState<ContextMenuPosition | null>(null)
  const [linkModal, setLinkModal] = useState<LinkModalState | null>(null)
  const {
    toggleStar,
    toggleRead,
    archive,
    unarchive,
    bulkAction,
    setCategory,
    markAsQuoteRequest,
    bulkResetCategory,
    inviteEmailWatcher,
    createAutoJunkRule,
  } = useEmailActions()
  const markAsBill = useMarkEmailAsBill()
  const [quoteReqBusy, setQuoteReqBusy] = useState(false)
  const navigate = useNavigate()
  const { toast } = useToast()
  const personalCategories = usePersonalCategories()
  const orgCustomCategories = useOrgCustomCategories()

  const applyCategory = useCallback(async (emailId: string, key: string, label: string) => {
    try {
      await setCategory(emailId, key)
      toast(`Pinned as “${label}”`, 'success')
      onRefetch?.()
    } catch (err) {
      toast(`Could not set category: ${(err as Error).message || 'unknown error'}`, 'error')
    }
  }, [setCategory, toast, onRefetch])

  const applyQuoteRequest = useCallback(async (emailId: string, subject: string) => {
    setQuoteReqBusy(true)
    try {
      const res = await markAsQuoteRequest(emailId)
      toast(
        res?.already_quote_request
          ? 'Already a Quote Request - re-running the tracker'
          : 'Marked as Quote Request - building the tracker entry',
        'success',
      )
      onRefetch?.()
    } catch (err) {
      toast(
        `Could not mark "${subject}" as Quote Request: ${(err as Error).message || 'unknown error'}`,
        'error',
      )
    } finally {
      setQuoteReqBusy(false)
    }
  }, [markAsQuoteRequest, toast, onRefetch])

  const applyAutoJunk = useCallback(async (
    emailId: string,
    matchType: 'sender_domain' | 'sender_exact',
  ) => {
    onResolvedLocal?.(emailId)
    try {
      const res = await createAutoJunkRule(emailId, matchType)
      const n = res?.binned_count ?? 0
      toast(
        res?.created
          ? `Junk rule for ${res.rule.match_value} created - binned ${n} email${n === 1 ? '' : 's'}. Undo in Tags & Rules.`
          : `Re-applied junk rule for ${res.rule.match_value} - binned ${n} more.`,
        'success',
      )
      onRefetch?.()
    } catch (err) {
      toast(`Could not create junk rule: ${(err as Error).message || 'unknown error'}`, 'error')
      onRefetch?.()
    }
  }, [createAutoJunkRule, toast, onRefetch, onResolvedLocal])

  const resetCategory = useCallback(async (emailId: string) => {
    try {
      await bulkResetCategory([emailId])
      toast('Cleared override - AI will re-classify', 'success')
      onRefetch?.()
    } catch (err) {
      toast(`Could not reset: ${(err as Error).message || 'unknown error'}`, 'error')
    }
  }, [bulkResetCategory, toast, onRefetch])

  const handleContextMenu = useCallback((e: React.MouseEvent, email: Email) => {
    e.preventDefault()
    e.stopPropagation()
    const emailIds = resolveContextIds?.(email) ?? [email.id]
    setCtxMenu({
      x: e.clientX,
      y: e.clientY,
      email,
      emailIds: emailIds.length > 0 ? emailIds : [email.id],
      pointerAddress: addressFromEventTarget(e.target),
    })
  }, [resolveContextIds])

  const openCompose = useCallback(
    (intent: 'reply' | 'reply_all' | 'forward', emailId: string) => {
      useComposeDockStore.getState().openOrRestore(seedFromIntent(intent, emailId))
    },
    [],
  )

  const kanbanApi = useMemo(() => getSamKanbanApi(), [])
  const { session } = useApiAuth()
  const orgId = resolveKanbanOrgId(session, {
    runtimeId: getRuntimeQuotechOrgId(),
    buildtimeId: getQuotechOrgIdOverride(),
  }) ?? null
  const handleKanbanAllocated = useCallback(() => {
    setCtxMenu(null)
    onRefetch?.()
  }, [onRefetch])
  const queryClient = useQueryClient()
  const [createBoardFor, setCreateBoardFor] = useState<{ emailIds: string[] } | null>(null)
  const [requestRuleFor, setRequestRuleFor] = useState<RequestRuleState | null>(null)
  const canRequestRule =
    typeof kanbanApi.proposeRuleRequest === 'function' &&
    typeof kanbanApi.applyRuleRequest === 'function' &&
    orgId != null
  const kanbanAllocate = useKanbanQuickAllocate({
    api: kanbanApi,
    orgId,
    notify: (kind, message) => toast(message, kind),
    currentUserId: session?.user?.id ?? null,
  })

  const handleCreateBoardAndAllocate = useCallback(
    async (input: CreateBoardInput): Promise<{ id: string }> => {
      const created = await kanbanApi.createBoard(buildBoardCreatePayload(input))
      if (input.cloneFromBoardId) {
        try {
          await clearProvisionedLanes(kanbanApi, created.id)
          const templateLanes = await kanbanApi.listLanes(input.cloneFromBoardId)
          const sorted = templateLanes
            .filter((lane) => !lane.archived)
            .sort((a, b) => a.position - b.position)
          for (let i = 0; i < sorted.length; i++) {
            const lane = sorted[i]
            await kanbanApi.createLane({
              board: created.id,
              name: lane.name,
              kind: lane.kind,
              color: lane.color,
              wip_limit: lane.wip_limit,
              position: i,
            } as never)
          }
          if (input.cloneConfig !== false) {
            const template = await kanbanApi.getBoard(input.cloneFromBoardId)
            if (template.config_json) {
              await kanbanApi.updateBoard(created.id, {
                config_json: template.config_json,
              } as never)
            }
          }
        } catch {
          // Non-fatal: the operator gets the board and can add lanes.
        }
      } else {
        try {
          await kanbanApi.provisionLanes(created.id)
        } catch {
          // Non-fatal: the operator can add lanes manually.
        }
      }
      await queryClient.invalidateQueries({ queryKey: ['kanban-boards'] })

      const emailIds = createBoardFor?.emailIds ?? []
      if (emailIds.length > 0) {
        try {
          const lanes = await kanbanApi.listLanes(created.id, { withCounts: true })
          const laneId = pickDefaultLaneId(lanes, { boardKind: created.kind })
          if (laneId) {
            const outcome = emailIds.length > 1
              ? await kanbanAllocate.runMany({
                  emailIds,
                  boardId: created.id,
                  laneId,
                  source: 'USER_MANUAL',
                })
              : await kanbanAllocate.run({
                  emailId: emailIds[0],
                  boardId: created.id,
                  laneId,
                  source: 'USER_MANUAL',
                })
            if (outcome.kind === 'ok' || outcome.kind === 'conflict' || outcome.kind === 'batch') {
              onRefetch?.()
            }
          } else {
            toast('Board created - add a lane to allocate this email.', 'info')
          }
        } catch {
          toast('Board created, but allocating the email failed - open the board to drop it in.', 'error')
        }
      }
      return { id: created.id }
    },
    [kanbanApi, queryClient, kanbanAllocate, createBoardFor, toast, onRefetch],
  )

  useEffect(() => {
    const warm = () => {
      void queryClient.prefetchQuery({
        queryKey: orgId != null ? ['kanban-boards', orgId] : ['kanban-boards'],
        queryFn: () =>
          kanbanApi.listBoards(
            orgId != null ? { organisationId: orgId } : undefined,
          ),
        staleTime: 30_000,
      })
    }
    const ric = (
      globalThis as unknown as {
        requestIdleCallback?: (cb: () => void) => number
      }
    ).requestIdleCallback
    if (typeof ric === 'function') {
      const handle = ric(warm)
      return () => {
        const cancel = (
          globalThis as unknown as {
            cancelIdleCallback?: (h: number) => void
          }
        ).cancelIdleCallback
        if (typeof cancel === 'function') cancel(handle)
      }
    }
    const timeout = setTimeout(warm, 600)
    return () => clearTimeout(timeout)
  }, [queryClient, kanbanApi, orgId])

  return {
    applyAutoJunk,
    applyCategory,
    applyQuoteRequest,
    archive,
    bulkAction,
    canRequestRule,
    createBoardFor,
    ctxMenu,
    handleContextMenu,
    handleCreateBoardAndAllocate,
    handleKanbanAllocated,
    inviteEmailWatcher,
    inviteMembers,
    kanbanApi,
    linkModal,
    markAsBill,
    navigate,
    onPatchLocal,
    onRefetch,
    onResolvedLocal,
    openCompose,
    orgCustomCategories,
    orgId,
    personalCategories,
    quoteReqBusy,
    requestRuleFor,
    resetCategory,
    session,
    setCreateBoardFor,
    setCtxMenu,
    setLinkModal,
    setRequestRuleFor,
    toast,
    toggleRead,
    toggleStar,
    unarchive,
  }
}

export type EmailContextMenuController = ReturnType<typeof useEmailContextMenuController>
